// Shared types for the SEO landing-page content layer.
//
// Each topic page (10 topic pages + /ai-summary) is authored once per
// supported locale and rendered through the same <SeoPageLayout>. Content
// is bundle-time TypeScript — there is no user input — so HTML inside
// `body` strings is safe to render via dangerouslySetInnerHTML.

export type SeoLocale = "en" | "es" | "fr-CA" | "pa-Guru";

export const SEO_LOCALES: readonly SeoLocale[] = [
  "en",
  "es",
  "fr-CA",
  "pa-Guru",
] as const;

// hreflang BCP-47 codes Google indexes by. We strip the script tag from
// pa-Guru → pa because Google's hreflang documentation prefers the bare
// language subtag for Punjabi; the in-app i18next code remains pa-Guru
// for the family experience.
export const HREFLANG_CODE: Record<SeoLocale, string> = {
  "en": "en",
  "es": "es",
  "fr-CA": "fr-CA",
  "pa-Guru": "pa",
};

// HTML lang attribute for the <main lang="…"> wrapper on each page so
// screen readers pronounce non-English copy correctly.
export const HTML_LANG: Record<SeoLocale, string> = {
  "en": "en",
  "es": "es",
  "fr-CA": "fr-CA",
  "pa-Guru": "pa",
};

// JSON-LD inLanguage value, BCP-47.
export const JSONLD_LANG: Record<SeoLocale, string> = {
  "en": "en",
  "es": "es",
  "fr-CA": "fr-CA",
  "pa-Guru": "pa",
};

export interface SeoSectionContent {
  heading: string;
  // Trusted bundle-time HTML. May contain <p>, <ul>, <ol>, <li>, <em>,
  // <strong>, <a>. Lists use the same Tailwind classes as the original
  // hand-rolled JSX so the rendered design is unchanged.
  html: string;
}

export interface SeoFaqContent {
  q: string;
  a: string;
}

export interface SeoRelatedRef {
  // Slug into SEO_PAGE_PATHS; the layout resolves the localized href.
  slug: import("./manifest").SeoSlug;
  label: string;
}

export interface SeoPageContent {
  title: string;
  description: string;
  breadcrumbLabel: string;
  eyebrow: string;
  h1: string;
  leadParagraph: string;
  // Optional 2–4 sentence AI answer block. Rendered in a visually subtle
  // <div id="ai-answer"> between the lead paragraph and the first H2 so
  // AI crawlers (ChatGPT, Perplexity, Claude, Gemini) can extract a clean
  // factual summary of the page without requiring a full JS render.
  // Plain prose only — no HTML, no markdown. English only for now.
  aiAnswer?: string;
  sections: SeoSectionContent[];
  faq?: SeoFaqContent[];
  related: SeoRelatedRef[];
  // /how-it-works hides the "See how JINGLE works" secondary CTA so it
  // doesn't link to itself. Set true on that page only.
  isHowItWorks?: boolean;
}

export type SeoPageContentByLocale = Record<SeoLocale, SeoPageContent>;
